import { DEFAULT_LESSONS_PAGE_SIZE } from '@/constant';
import { capture } from '@/handlers/error';
import { ILesson } from '@/models/lesson';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { parseSlide, Slide } from '@/types/slide';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useNotifications } from '@toolpad/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteDialog } from '../../components/dialogs/DeleteDialog';
import { useSchool, useSession } from '../../providers/session/SessionProvider';
import { CreateHomeworkDialog, FormValues } from './components/CreateHomeworkDialog';
import HomeworkManagerTable from './components/HomeworkManagerTable';
import { HomeworkPageToolbar } from './components/HomeworkPageHeader';
import LessonPreviewDialog from './components/LessonPreviewDialog';
import { parseFilterModel } from './filter';
import { GET_CLASSES, GET_HOMEWORKS, GET_LESSON, GET_LESSONS, GetClassesResponse, GetHomeworksResponse, GetLessonResponse, GetLessonsResponse, GetLessonVariables } from './gql';
import useHomework from './hook/useHomework';
import { ClassModel, HomeworkData, HomeworkStatus } from './types/homework';
const defaultSortModel: { field: string; sort: 'asc' | 'desc' } = { field: 'dateCreated', sort: 'desc' };

export default function HomeworkPage() {
    const [lessons, setLessons] = useState<ILesson[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState<{ count: number; rows: HomeworkData[] } | undefined>(undefined);
    const [showCreateHomeworkDialog, setShowCreateHomeworkDialog] = React.useState(false);
    const [classData, setClassData] = React.useState<ClassModel[]>([]);
    const [showDeleteHomeworkPopupOpen, setShowDeleteHomeworkPopupOpen] = React.useState(false);
    const { session } = useSession();
    const { school } = useSchool();
    const [queryOption, setQueryOption] = React.useState<{ pagination?: GridPaginationModel; sort?: GridSortModel; filter?: GridFilterModel } | undefined>(undefined);
    const notifications = useNotifications();
    const client = useApolloClient();
    const { t } = useTranslation();
    const [openPreview, setOpenPreview] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<string[] | undefined>(undefined);

    const [querySelectedLesson] = useLazyQuery<GetLessonResponse, GetLessonVariables>(GET_LESSON);
    const { data: dataLessons, refetch } = useQuery<GetLessonsResponse>(GET_LESSONS, {
        variables: {
            skip: 0,
            take: DEFAULT_LESSONS_PAGE_SIZE,
            where: {
                AND: [{ lessonVersions: { some: { published: { equals: false } } } }, { archived: { equals: false } }],
            },
        },
    });

    const [lessonSelected, setLessonSelected] = React.useState<{
        id: string;
        lessonName: string;
        dateUpdated: string;
        slides: Slide[];
    } | null>(null);

    const refresh = React.useCallback(async () => {
        // Skip if we don't have required data
        if (!school?.id || !session?.token) {
            setData(undefined);
            return;
        }
        // Set loading and capture current query options to prevent race conditions
        setLoading(true);
        const filters = queryOption?.filter ? parseFilterModel(queryOption.filter) : [];
        try {
            const getHomeworkResponse = await client.query<GetHomeworksResponse>({
                query: GET_HOMEWORKS,
                fetchPolicy: 'network-only',
                variables: {
                    skip: (queryOption?.pagination?.page ?? 0) * (queryOption?.pagination?.pageSize ?? 20),
                    take: queryOption?.pagination?.pageSize ?? 20,
                    schoolId: school?.id,
                    orderBy: queryOption?.sort?.[0] ? [{ [queryOption.sort[0].field]: queryOption.sort[0].sort }] : [{ [defaultSortModel.field]: defaultSortModel.sort }],
                    where: {
                        isArchived: {
                            equals: false,
                        },
                        AND: [
                            {
                                teacher: {
                                    id: {
                                        equals: session?.user?.teacherProfile?.id ?? '',
                                    },
                                },
                            },
                            ...filters,
                        ],
                    },
                },
            });
            setData({
                count: getHomeworkResponse.data.homeworksCount,
                rows: getHomeworkResponse.data.homeworks.map((homework) => ({
                    id: homework.id,
                    startTime: new Date(homework.startTime).toISOString(),
                    endTime: new Date(homework.endTime).toISOString(),
                    lessonName: homework.lessonVersion?.lessonName,
                    homeworkName: homework.homeworkName,
                    status: homework.isArchived ? HomeworkStatus.archived : new Date(homework.serverTime) > new Date(homework.endTime) ? HomeworkStatus.ended : HomeworkStatus.started,
                    classes: homework.class || [],
                })),
            });
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            capture(error);
        } finally {
            setLoading(false);
        }
    }, [client, notifications, queryOption, school?.id, session?.token, session?.user?.teacherProfile?.id]);

    const handleDeleteHomework = (ids: string[]) => {
        if (!ids) {
            return;
        }
        setDeleteId(ids);
        setShowDeleteHomeworkPopupOpen(true);
    };

    const onClosePreview = () => {
        setOpenPreview(false);
    };

    const { publish, remove } = useHomework();

    const handleCreateHomework = () => {
        setShowCreateHomeworkDialog(true);
    };

    const onCreateHomeworkDialogClose = async (values: FormValues | null) => {
        if (values) {
            try {
                const res = await publish(values);
                if (!res.id || res.id === '') {
                    notifications.show(t('homework.createFailed'), { severity: 'error', autoHideDuration: 5000 });
                    capture(new Error('Failed to create homework: No ID returned'));
                    return;
                }
                notifications.show(t('homework.createSuccess'), { severity: 'success', autoHideDuration: 5000 });
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : t('homework.createFailed'), { severity: 'error', autoHideDuration: 5000 });
                capture(error);
                return;
            }
            setShowCreateHomeworkDialog(false);
            refresh();
        } else {
            setShowCreateHomeworkDialog(false);
        }
    };

    const { data: classQueryData, error: classQueryError } = useQuery<GetClassesResponse>(GET_CLASSES, {
        fetchPolicy: 'network-only',
    });

    const handleSearchLessons = useCallback(
        async (searchQuery: string) => {
            try {
                if (!searchQuery.trim()) {
                    //reset lessons
                    setLessons([]);
                }

                // Create search filter for lesson names
                const searchFilter = {
                    lessonVersions: {
                        some: {
                            lessonName: {
                                contains: searchQuery.trim(),
                                mode: 'insensitive',
                            },
                        },
                    },
                };

                // Use refetch to update dataLessons automatically
                await refetch({
                    skip: 0,
                    take: DEFAULT_LESSONS_PAGE_SIZE,
                    where: {
                        AND: [
                            {
                                lessonVersions: { some: { published: { equals: false } } },
                            },
                            { archived: { equals: false } },
                            searchFilter,
                        ],
                    },
                });
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : 'Failed to search lessons', { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            }
        },
        [refetch, notifications],
    );

    React.useEffect(() => {
        if (classQueryError) {
            notifications.show(classQueryError.message, {
                severity: 'error',
                autoHideDuration: 5000,
            });
        } else if (classQueryData) {
            setClassData(classQueryData.classes);
        }
    }, [classQueryData, classQueryError, notifications]);

    React.useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!dataLessons) return;

        const baseLesson: ILesson = {
            id: '',
            latestLessonVersion: {
                lessonName: '',
            },
            latestLessonDraft: {
                id: '',
                lessonName: '',
                dateUpdated: '',
                published: true,
                slides: [],
            },
            shareWithPublic: false,
            sharesCount: 0,
            shares: [],
            teacher: {
                id: '',
                user: {
                    name: '',
                    email: '',
                },
            },
        };

        const newLessons: ILesson[] = dataLessons.lessons.map((lesson) => ({
            ...baseLesson,
            ...lesson,
            latestLessonDraft: {
                ...baseLesson.latestLessonDraft,
                ...lesson.latestLessonDraft,
            },
        }));
        setLessons(newLessons);
    }, [dataLessons]);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: 16 }}>
                <Stack sx={{ flex: 1, overflow: 'hidden' }} spacing={2}>
                    <HomeworkPageToolbar onCreateHomework={handleCreateHomework} />
                    <HomeworkManagerTable onCreateHomework={handleCreateHomework} onDeleteHomework={handleDeleteHomework} onQueryOptionUpdate={setQueryOption} rows={data} loading={loading} />
                </Stack>
                {showCreateHomeworkDialog && (
                    <CreateHomeworkDialog
                        open={showCreateHomeworkDialog}
                        payload={{
                            data: { classesData: classData, lessons: lessons ?? [] },
                        }}
                        onSearch={async (value) => {
                            await handleSearchLessons(value);
                        }}
                        onClose={onCreateHomeworkDialogClose}
                        onPreview={async (id: string) => {
                            if (id) {
                                const res = await querySelectedLesson({ variables: { where: { id } } });
                                if (res?.data?.lesson?.latestLessonDraft) {
                                    const dateUpdated = res.data.lesson.latestLessonDraft.dateUpdated;
                                    const lessonName = res.data.lesson.latestLessonDraft.lessonName;
                                    const slides = res.data.lesson.latestLessonDraft.slides.map((slide) => parseSlide(slide));
                                    slides?.sort((a, b) => a.sort - b.sort);
                                    setLessonSelected({ id, lessonName, dateUpdated, slides });
                                    setOpenPreview(true);
                                }
                            }
                        }}
                    />
                )}
                {openPreview && lessonSelected && (
                    <EditorProvider>
                        <LessonPreviewDialog open={openPreview} handleClose={onClosePreview} lesson={lessonSelected} />
                    </EditorProvider>
                )}
                {showDeleteHomeworkPopupOpen && (
                    <DeleteDialog
                        title={t('homework.delete.title')}
                        description={t('homework.delete.description')}
                        description2={t('homework.delete.description2')}
                        payload={null}
                        open={showDeleteHomeworkPopupOpen}
                        onClose={async (confirm: boolean) => {
                            if (confirm) {
                                if (deleteId) {
                                    try {
                                        await remove(deleteId);
                                    } catch (error) {
                                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                        capture(error);
                                    }
                                    refresh();
                                }
                            }
                            setShowDeleteHomeworkPopupOpen(false);
                        }}
                    />
                )}
            </div>
        </>
    );
}
